import { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL, HEADERS } from "../../../utils/constant";
import { useCommonContext } from "../../../utils/context";
import { useTypeConfig } from "../../../utils/useTypeConfig";
import PermissionModal from "../PermissionModel";

const CustomerModal = ({ show, handleClose, customerData, fetchCustomers }) => {
  const { fetchCustomers: getCustomers } = useCommonContext();
  const { labels, hiddenFields } = useTypeConfig();
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    customerName: "",
    companyName: "",
    mobileNo: "",
    emailId: "",
    customerType: "",
    brandNames: [""],
    modelNo: "",
    primaryAddress: "",
    landmarkPACI: "",
    country: "",
    state: "",
    city: "",
    zipcode: "",
    region: "",
  });

  const [errors, setErrors] = useState({});

  const [allowedPermissions, setAllowedPermissions] = useState([]);

  const resetForm = () => {
    setCustomerDetails({
      customerName: "",
      companyName: "",
      mobileNo: "",
      emailId: "",
      customerType: "",
      brandNames: [""],
      modelNo: "",
      primaryAddress: "",
      landmarkPACI: "",
      country: "",
      state: "",
      city: "",
      zipcode: "",
      region: "",
    });
    setErrors({});
    setAllowedPermissions([]);
  };

  useEffect(() => {
    if (customerData) {
      setCustomerDetails({
        customerName: customerData.customer_name || "",
        companyName: customerData.company_name || "",
        mobileNo: customerData.phone_number || "",
        emailId: customerData.email || "",
        customerType: customerData.customer_type || "",
        brandNames: (customerData.brand_names || []).map((brand) => brand.name),
        modelNo: customerData.model_no || "",
        primaryAddress: customerData.primary_address || "",
        landmarkPACI: customerData.landmark_paci || "",
        country: customerData.country || "",
        state: customerData.state || "",
        city: customerData.city || "",
        zipcode: customerData.zipcode || "",
        region: customerData.region || "",
      });
      setAllowedPermissions(customerData.user_permissions || []);
    } else {
      resetForm();
    }
  }, [customerData, show]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setCustomerDetails((prev) => ({
      ...prev,
      [id]: value,
    }));

    if (id === "mobileNo" || id === "zipcode") {
      const numericValue = value.replace(/\D/g, "").replace(/^0+/, "");
      setCustomerDetails({ ...customerDetails, [id]: numericValue });
    } else {
      setCustomerDetails({ ...customerDetails, [id]: value });
    }

    setErrors({ ...errors, [id]: "" });
  };

  const handleBrandNameChange = (e, index) => {
    const { value } = e.target;
    const newBrandNames = [...customerDetails.brandNames];
    newBrandNames[index] = value;
    setCustomerDetails((prev) => ({
      ...prev,
      brandNames: newBrandNames,
    }));
  };

  const addBrandNameField = () => {
    setCustomerDetails((prev) => ({
      ...prev,
      brandNames: [...prev.brandNames, ""],
    }));
  };

  const removeBrandNameField = (index) => {
    const newBrandNames = customerDetails.brandNames.filter(
      (_, i) => i !== index,
    );
    setCustomerDetails((prev) => ({
      ...prev,
      brandNames: newBrandNames,
    }));
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = {};

    if (customerData) {
    } else {
      if (!customerDetails.emailId.trim()) {
        newErrors.emailId = "Email is required.";
        valid = false;
      } else if (!/\S+@\S+\.\S+/.test(customerDetails.emailId)) {
        newErrors.emailId = "Email is invalid.";
        valid = false;
      }

      if (!customerDetails.mobileNo.trim()) {
        newErrors.mobileNo = "Mobile number is required.";
        valid = false;
      } else if (!/^\d{10}$/.test(customerDetails.mobileNo)) {
        newErrors.mobileNo = "Mobile number must be 10 digits.";
        valid = false;
      }
    }

    if (!customerDetails.customerName.trim()) {
      newErrors.customerName = "Name is required.";
      valid = false;
    }

    if (!customerDetails.primaryAddress.trim()) {
      newErrors.primaryAddress = "Address is required.";
      valid = false;
    }

    if (!customerDetails.landmarkPACI.trim()) {
      newErrors.landmarkPACI = "Landmark is required.";
      valid = false;
    }

    if (!customerDetails.country.trim()) {
      newErrors.country = "Country is required.";
      valid = false;
    }

    if (!customerDetails.state.trim()) {
      newErrors.state = "State is required.";
      valid = false;
    }

    if (!hiddenFields.includes("product_name")) {
      if (
        !customerDetails.brandNames ||
        customerDetails.brandNames.length === 0
      ) {
        newErrors.brandNames = "At least one product name is required.";
        valid = false;
      } else if (customerDetails.brandNames.some((name) => !name.trim())) {
        newErrors.brandNames = "Each product name must be non-empty.";
        valid = false;
      }
    }

    if (!customerDetails.city.trim()) {
      newErrors.city = "City is required.";
      valid = false;
    }

    if (!customerDetails.zipcode.trim()) {
      newErrors.zipcode = "Zipcode is required.";
      valid = false;
    } else if (!/^\d{6}$/.test(customerDetails.zipcode)) {
      newErrors.zipcode = "Zipcode must be a 6-digit number.";
      valid = false;
    }

    if (!customerDetails.region.trim()) {
      newErrors.region = "Region is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const payload = {
      email: customerDetails.emailId,
      phone_number: customerDetails.mobileNo,
      customer_name: customerDetails.customerName,
      company_name: customerDetails.companyName,
      brand_names: customerDetails.brandNames.filter(
        (name) => name.trim() !== "",
      ),
      model_no: customerDetails.modelNo,
      customer_type: customerDetails.customerType,
      primary_address: customerDetails.primaryAddress,
      landmark_paci: customerDetails.landmarkPACI,
      country: customerDetails.country,
      state: customerDetails.state,
      city: customerDetails.city,
      zipcode: customerDetails.zipcode,
      region: customerDetails.region,
      role: "customer",
    };

    if (
      JSON.stringify(allowedPermissions) !==
      JSON.stringify(customerDetails.user_permissions)
    ) {
      payload.user_permissions = allowedPermissions;
    }

    try {
      let response;

      if (customerData) {
        response = await axios.put(
          `${BASE_URL}/users/tms_users/${customerData.id}/`,
          payload,
          { headers: HEADERS },
        );
      } else {
        response = await axios.post(`${BASE_URL}/users/tms_users/`, payload, {
          headers: HEADERS,
        });
      }

      if (response) {
        handleClose();
        toast.success(
          `${labels.customer} ${customerData ? "updated" : "added"} successfully!`,
        );
        fetchCustomers();
        getCustomers();
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const err = error.response.data;

        if (err.error) {
          const errorData = err.error;

          if (typeof errorData === "object" && !Array.isArray(errorData)) {
            Object.entries(errorData).forEach(([field, messages]) => {
              if (Array.isArray(messages)) {
                messages.forEach((msg) => toast.error(msg));
              } else {
                toast.error(messages);
              }
            });
          }

          return;
        }

        if (Array.isArray(err)) {
          err.forEach((msg) => toast.error(msg));
          return;
        }

        if (typeof err === "string") {
          toast.error(err);
          return;
        }
      }

      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        resetForm();
        handleClose();
      }}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {customerData ? `Edit ${labels.customer}` : `Add ${labels.customer}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="mb-3">
            <Col>
              <Form.Group
                controlId="customerName"
                className="form-floating mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder={labels.customerName}
                  value={customerDetails.customerName}
                  isInvalid={!!errors.customerName}
                  onChange={handleInputChange}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {errors.customerName}
                </Form.Control.Feedback>
                <Form.Label>{labels.customerName}*</Form.Label>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group controlId="mobileNo" className="form-floating ">
                <Form.Control
                  type="text"
                  placeholder="Mobile No."
                  value={customerDetails.mobileNo}
                  onChange={handleInputChange}
                  required
                  isInvalid={!!errors.mobileNo}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.mobileNo}
                </Form.Control.Feedback>
                <Form.Label>Mobile No*</Form.Label>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Form.Group controlId="emailId" className="form-floating mb-3">
                <Form.Control
                  type="email"
                  placeholder="Email Id"
                  value={customerDetails.emailId}
                  onChange={handleInputChange}
                  isInvalid={!!errors.emailId}
                  // disabled={!!customerData}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {errors.emailId}
                </Form.Control.Feedback>
                <Form.Label>Email Id*</Form.Label>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group
                controlId="companyName"
                className="form-floating mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder={labels.companyName}
                  value={customerDetails.companyName}
                  onChange={handleInputChange}
                  isInvalid={!!errors.companyName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.companyName}
                </Form.Control.Feedback>
                <Form.Label>{labels.companyName} </Form.Label>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            {!hiddenFields.includes("model") && (
              <Col>
                <Form.Group controlId="modelNo" className="form-floating mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Model No"
                    value={customerDetails.modelNo}
                    onChange={handleInputChange}
                  />
                  <Form.Label>Model No</Form.Label>
                </Form.Group>
              </Col>
            )}
            <Col>
              <Form.Group controlId="region" className="form-floating mb-3">
                <Form.Select
                  value={customerDetails.region}
                  onChange={handleInputChange}
                  required
                  isInvalid={!!errors.region}
                >
                  <option value="" disabled>
                    Select Region
                  </option>
                  <option value="North">North</option>
                  <option value="West">West</option>
                  <option value="East">East</option>
                  <option value="South">South</option>
                </Form.Select>
                <Form.Label>Region*</Form.Label>

                <Form.Control.Feedback type="invalid">
                  {errors.region}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group
                controlId="primaryAddress"
                className="form-floating mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Primary Address"
                  value={customerDetails.primaryAddress}
                  onChange={handleInputChange}
                  required
                  isInvalid={!!errors.primaryAddress}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.primaryAddress}
                </Form.Control.Feedback>
                <Form.Label>Address*</Form.Label>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group
                controlId="landmarkPACI"
                className="form-floating mb-3"
              >
                <Form.Control
                  type="text"
                  placeholder="Landmark / PACI"
                  value={customerDetails.landmarkPACI}
                  onChange={handleInputChange}
                  required
                  isInvalid={!!errors.landmarkPACI}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.landmarkPACI}
                </Form.Control.Feedback>
                <Form.Label>Landmark*</Form.Label>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="city" className="form-floating mb-3">
                <Form.Control
                  type="text"
                  placeholder="City"
                  value={customerDetails.city}
                  onChange={handleInputChange}
                  required
                  isInvalid={!!errors.city}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.city}
                </Form.Control.Feedback>
                <Form.Label>City*</Form.Label>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="state" className="form-floating mb-3">
                <Form.Control
                  type="text"
                  placeholder="State"
                  value={customerDetails.state}
                  onChange={handleInputChange}
                  required
                  isInvalid={!!errors.state}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.state}
                </Form.Control.Feedback>
                <Form.Label>State*</Form.Label>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="zipcode" className="form-floating mb-3">
                <Form.Control
                  type="text"
                  placeholder="Zip Code"
                  value={customerDetails.zipcode}
                  onChange={handleInputChange}
                  required
                  isInvalid={!!errors.zipcode}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.zipcode}
                </Form.Control.Feedback>
                <Form.Label>Zip Code*</Form.Label>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="country" className="form-floating mb-3">
                <Form.Control
                  type="text"
                  placeholder="Country"
                  value={customerDetails.country}
                  onChange={handleInputChange}
                  required
                  isInvalid={!!errors.country}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.country}
                </Form.Control.Feedback>
                <Form.Label>Country*</Form.Label>
              </Form.Group>
            </Col>
          </Row>
          {!hiddenFields.includes("product_name") && (
            <Row className="mb-3">
              <Form.Group controlId="brandNames">
                <Form.Label>Product Name*</Form.Label>
                {customerDetails.brandNames.map((brandName, index) => (
                  <div key={index} className="mb-2">
                    <div className="d-flex align-items-start">
                      <Form.Control
                        type="text"
                        placeholder={`Product Name ${index + 1}`}
                        value={brandName}
                        onChange={(e) => handleBrandNameChange(e, index)}
                        className="me-2"
                        required
                        isInvalid={
                          !!errors.brandNames && errors.brandNames[index]
                        }
                      />
                      <Button
                        variant="outline-danger"
                        onClick={() => removeBrandNameField(index)}
                        disabled={customerDetails.brandNames.length <= 1}
                        className="align-self-center"
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ))}
                <Button variant="outline-primary" onClick={addBrandNameField}>
                  Add Another Product
                </Button>
                {errors.brandNames && typeof errors.brandNames === "string" && (
                  <div
                    className="text-danger mt-2"
                    style={{ fontSize: "0.875rem" }}
                  >
                    {errors.brandNames}
                  </div>
                )}
              </Form.Group>
            </Row>
          )}
          {/* <Row className="mb-3 mt-3">
            <Col xs="auto">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowPermissionModal(true)}
              >
                {customerData ? "Edit Permissions" : "Select Permissions"}
              </Button>
            </Col>
          </Row> */}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleFormSubmit} disabled={loading}>
          {loading ? (
            <Spinner animation="border" role="status" size="sm">
              <span className="visually-hidden"></span>
            </Spinner>
          ) : customerData ? (
            "Submit"
          ) : (
            "Submit"
          )}
        </Button>
      </Modal.Footer>
      {showPermissionModal && (
        <PermissionModal
          show={showPermissionModal}
          handleClose={() => setShowPermissionModal(false)}
          setAllowedPermissions={setAllowedPermissions}
          userEditDetails={{ permissions: allowedPermissions }}
          agentData={customerData}
        />
      )}
    </Modal>
  );
};

export default CustomerModal;
